// src/hooks/useEmailService.js
import { useAuth } from './useAuth';

// The function URL is relative - Netlify handles the base URL
const EMAIL_FUNCTION_URL = '/.netlify/functions/send-email';
// Hardcode your domain here
const APP_DOMAIN = 'https://jira-tracker.co.uk';

export const useEmailService = () => {
  const { userProfile } = useAuth();

  const sendEmail = async ({ to, subject, html, type }) => {
    try {
      console.log('📧 Attempting to send email:', { to, subject, type });
      
      const response = await fetch(EMAIL_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          subject,
          html,
          type
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Email send failed:', data.error);
        throw new Error(data.error || 'Failed to send email');
      }

      console.log('✅ Email sent successfully:', data);
      return data;
    } catch (error) {
      console.error('📧 Email service error:', error);
      throw error;
    }
  };

  const sendIssueAssignmentEmail = async (issue, assigneeEmail, assigneeName) => {
    console.log('🔄 sendIssueAssignmentEmail called:', { 
      issueId: issue.id, 
      issueKey: issue.issueKey,
      assigneeEmail, 
      assigneeName 
    });

    // Use hardcoded domain - no dynamic fallback
    const issueUrl = `${APP_DOMAIN}/issues/${issue.id}`;
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #3b82f6; color: white; padding: 20px; text-align: center; }
          .content { background: #f9fafb; padding: 20px; }
          .button { display: inline-block; padding: 12px 24px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-weight: bold; }
          .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 14px; }
          .url { word-break: break-all; color: #6b7280; font-size: 12px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>You've Been Assigned to an Issue</h1>
          </div>
          <div class="content">
            <h2>${issue.issueKey} - ${issue.title}</h2>
            <p><strong>Description:</strong> ${issue.description || 'No description provided'}</p>
            <p><strong>Priority:</strong> ${issue.priority}</p>
            <p><strong>Type:</strong> ${issue.type}</p>
            <p><strong>Reporter:</strong> ${issue.creatorName}</p>
            <p><strong>Assigned to:</strong> ${assigneeName}</p>
            <br>
            <a href="${issueUrl}" class="button">View Issue</a>
            <div class="url">
              Can't click the button? Copy and paste this link:<br>
              ${issueUrl}
            </div>
          </div>
          <div class="footer">
            <p>This email was sent from Jira Tracker.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    return await sendEmail({
      to: assigneeEmail,
      subject: `[${issue.issueKey}] You've been assigned: ${issue.title}`,
      html,
      type: 'issue_assignment'
    });
  };

  const sendIssueUpdateEmail = async (issue, userEmail, userName, updates) => {
    console.log('🔄 sendIssueUpdateEmail called:', { 
      issueId: issue.id, 
      issueKey: issue.issueKey,
      userEmail, 
      userName,
      updates 
    });

    // Use hardcoded domain - no dynamic fallback
    const issueUrl = `${APP_DOMAIN}/issues/${issue.id}`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #10b981; color: white; padding: 20px; text-align: center; }
          .content { background: #f9fafb; padding: 20px; }
          .button { display: inline-block; padding: 12px 24px; background: #10b981; color: white; text-decoration: none; border-radius: 6px; font-weight: bold; }
          .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 14px; }
          ul { background: white; padding: 15px; border-radius: 6px; }
          .url { word-break: break-all; color: #6b7280; font-size: 12px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Issue Updated</h1>
          </div>
          <div class="content">
            <h2>${issue.issueKey} - ${issue.title}</h2>
            <p><strong>Updates:</strong></p>
            <ul>
              ${updates.map(update => `<li>${update}</li>`).join('')}
            </ul>
            <br>
            <a href="${issueUrl}" class="button">View Updated Issue</a>
            <div class="url">
              Can't click the button? Copy and paste this link:<br>
              ${issueUrl}
            </div>
          </div>
          <div class="footer">
            <p>This email was sent from Jira Tracker.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    return await sendEmail({
      to: userEmail,
      subject: `[${issue.issueKey}] Issue updated: ${issue.title}`,
      html,
      type: 'issue_update'
    });
  };

  const sendCommentNotificationEmail = async (issue, comment, recipientEmail, recipientName) => {
    console.log('🔄 sendCommentNotificationEmail called:', { 
      issueId: issue.id, 
      issueKey: issue.issueKey,
      recipientEmail, 
      recipientName,
      commentId: comment.id 
    });

    // Use hardcoded domain - no dynamic fallback
    const issueUrl = `${APP_DOMAIN}/issues/${issue.id}`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #8b5cf6; color: white; padding: 20px; text-align: center; }
          .content { background: #f9fafb; padding: 20px; }
          .button { display: inline-block; padding: 12px 24px; background: #8b5cf6; color: white; text-decoration: none; border-radius: 6px; font-weight: bold; }
          .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 14px; }
          .comment { background: white; padding: 15px; border-radius: 6px; border-left: 4px solid #8b5cf6; }
          .url { word-break: break-all; color: #6b7280; font-size: 12px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>New Comment on Issue</h1>
          </div>
          <div class="content">
            <h2>${issue.issueKey} - ${issue.title}</h2>
            <p><strong>Comment by:</strong> ${comment.authorName}</p>
            <div class="comment">
              <p><strong>Comment:</strong></p>
              <p>${comment.text}</p>
            </div>
            <br>
            <a href="${issueUrl}" class="button">View Issue & Comment</a>
            <div class="url">
              Can't click the button? Copy and paste this link:<br>
              ${issueUrl}
            </div>
          </div>
          <div class="footer">
            <p>This email was sent from Jira Tracker.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    return await sendEmail({
      to: recipientEmail,
      subject: `[${issue.issueKey}] New comment: ${issue.title}`,
      html,
      type: 'comment_notification'
    });
  };

  return {
    sendEmail,
    sendIssueAssignmentEmail,
    sendIssueUpdateEmail,
    sendCommentNotificationEmail
  };
};